import { type Transformation, buildSrc, getResponsiveImageAttributes } from "@imagekit/javascript";

import { getEnv } from "../env/env";

type FullGetImgUrlArgs = {
  imgPath?: string;
  encodeUri?: boolean;
  transformation?: Transformation[];
};
type GetImgUrlArgs = FullGetImgUrlArgs | string;

export const getImageUrlEndpoint = (): string => {
  return getEnv(
    process.env.NEXT_PUBLIC_IMG_CDN_ENDPOINT,
    "NEXT_PUBLIC_IMG_CDN_ENDPOINT",
  );
};

const getFrappeImageUrlEndpoint = (): string => {
  return getEnv(
    process.env.NEXT_PUBLIC_FRAPPE_BACKEND_URL,
    "NEXT_PUBLIC_FRAPPE_BACKEND_URL",
  );
};

const handleImageArgs = (args: GetImgUrlArgs): FullGetImgUrlArgs => {
  // e = effective
  let eImgPath: string | undefined;
  let eEncodeUri: boolean | undefined;
  let eTransformation: Transformation[] | undefined;

  if (typeof args === "string") {
    eImgPath = args;
  }
  else {
    const { imgPath, encodeUri = false, transformation } = args;
    eImgPath = imgPath;
    eEncodeUri = encodeUri;
    eTransformation = transformation;
  }

  return {
    imgPath: eImgPath,
    encodeUri: eEncodeUri,
    transformation: eTransformation,
  };
};

export const getImgUrl = (args: GetImgUrlArgs): string => {
  const { imgPath, encodeUri = false, transformation } = handleImageArgs(args);

  let src = imgPath;

  if (!src) {
    return "";
  }

  if (encodeUri) {
    src = encodeURIComponent(src);
  }

  if (src.match(/https?:\/\//)) {
    return src;
  }

  return buildSrc({
    src,
    transformation,
    urlEndpoint: getImageUrlEndpoint(),
  });
};

export const getBackEndHostname = (url?: string): string | undefined => {
  let backEndUrl = url;
  if (!url) {
    backEndUrl = getFrappeImageUrlEndpoint();
  }

  // eslint-disable-next-line no-useless-escape
  const backEndRegex = backEndUrl?.match(/https?:\/\/([\.\w\d-_:]+)/);
  if (backEndRegex?.length === 2) {
    return backEndRegex[1];
  }
  return backEndUrl;
};

export const stripErpFiles = (src: string): string => {
  let filename: string | undefined;

  // strip prefix "/files"
  if (src.startsWith("/files/")) {
    filename = src.replace("files/", "");
  }

  const splitSrcList = src.split("/");
  if (splitSrcList.length === 2 && splitSrcList[0] === "files") {
    filename = splitSrcList.pop();
  }
  if (filename) {
    return filename;
  }

  return src;
};

export const getImgUrlReplaceBe = (args: GetImgUrlArgs): string => {
  const { imgPath, encodeUri = false, transformation } = handleImageArgs(args);

  const backEndHostname = getBackEndHostname();

  let src = imgPath;

  const frappeImg = src?.match(
    // eslint-disable-next-line no-useless-escape
    new RegExp(`https?:/\/${backEndHostname}\/(.*)`),
  );

  if (frappeImg) {
    // eslint-disable-next-line prefer-destructuring
    src = frappeImg[1];
  }

  if (src) {
    src = stripErpFiles(src);
  }

  return getImgUrl({
    imgPath: src,
    encodeUri,
    transformation,
  });
};

export const getImgHtml = (html?: string): string[] => {
  let imgHtml: string[] = [];

  if (!html) {
    return [];
  }

  const matchImgHtml = html.match(/<img .*?>/gi);
  if (matchImgHtml) {
    imgHtml = matchImgHtml;
  }

  return imgHtml;
};

export const getImgSrc = (imgHtml: string): string => {
  const imgSrc = imgHtml.match(/src=['"](.*?)['"]/);
  let src = "";
  if (imgSrc) {
    // eslint-disable-next-line prefer-destructuring
    src = imgSrc[1];
  }
  return src;
};

export const getImgAttr = (imgHtml: string): { [key: string]: string } => {
  const imgAttr = imgHtml.matchAll(/ ([\w]+)=['"](.*?)["']/gi);
  const attrDict: { [key: string]: string } = {};

  Array.from(imgAttr).forEach((attr) => {
    let key = attr[1] ? attr[1] : "";
    const value = attr[2] ? attr[2] : "";
    if (key) {
      if (key === "class") {
        key = "className";
      }
      attrDict[key] = value;
    }
  });

  return attrDict;
};

export const addImgsetToImg = (imgHtml: string): string => {
  const imgAttr = getImgAttr(imgHtml);

  let { src: baseSrc } = imgAttr;
  const { alt, className } = imgAttr;

  if (!baseSrc.match(/https?:\/\//)) {
    baseSrc = stripErpFiles(baseSrc);

    const { sizes, srcSet, src } = getResponsiveImageAttributes({
      src: baseSrc,
      sizes: "(max-width: 1280px) 100vw, 1920px",
      urlEndpoint: getImageUrlEndpoint(),
    });

    return `
      <img
        src="${getImgUrl({
          imgPath: src,
          encodeUri: false,
        })}"
        srcset="${srcSet}"
        size="${sizes}"
        ${alt ? `alt="${alt}"` : ""}
        ${className ? `class="${className}"` : ""}
      />`;
  }

  return imgHtml;
};

export const convertHtmlImgToCdnImg = (html: string): string => {
  const imgTagList = getImgHtml(html);
  let convertedHtml = html;
  imgTagList.forEach((imgTag) => {
    convertedHtml = convertedHtml.replace(imgTag, addImgsetToImg(imgTag));
  });
  return convertedHtml;
};

export interface ImageTransformation extends Transformation {}

interface ImageLoaderProps {
  quality?: number;
  src: string;
  transformation?: ImageTransformation[];
  width: number;
}
export const imageLoader = (props: ImageLoaderProps): string => {
  const {
    src,
    width,
    quality = 85,
    transformation: transformationProps = [],
  } = props;

  const transformation: Transformation[] = [
    {
      width,
      quality,
      crop: "at_max",
    },
    ...transformationProps,
  ];

  return getImgUrlReplaceBe({
    imgPath: src,
    encodeUri: false,
    transformation,
  });
};
